import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

interface CallingBalance {
  totalCents: number;
  reservedCents: number;
  availableCents: number;
  currency: string;
  lastTopupAt: string | null;
  lowBalanceAlertCents: number;
  isLowBalance: boolean;
}

interface CallingTransaction {
  id: string;
  transaction_type: string;
  amount_cents: number;
  balance_before_cents: number;
  balance_after_cents: number;
  description: string;
  created_at: string;
}

interface BalanceResponse {
  success: boolean;
  balance: CallingBalance;
  transactions: CallingTransaction[];
  profile: {
    userType: string;
    totalCalls: number;
    totalSpendCents: number;
  } | null;
}

const EMPTY_BALANCE: CallingBalance = {
  totalCents: 0,
  reservedCents: 0,
  availableCents: 0,
  currency: "AUD",
  lastTopupAt: null,
  lowBalanceAlertCents: 500,
  isLowBalance: true,
};

const EMPTY_BALANCE_RESPONSE: BalanceResponse = {
  success: false,
  balance: EMPTY_BALANCE,
  transactions: [],
  profile: null,
};

/** Clear localStorage balance cache for a specific user (or all users) */
export function clearBalanceCache(userId?: string) {
  try {
    if (userId) {
      localStorage.removeItem(`elefa_balance_${userId}`);
    } else {
      // Clear all balance cache keys
      const keys = Object.keys(localStorage).filter(k => k.startsWith('elefa_balance_'));
      keys.forEach(k => localStorage.removeItem(k));
    }
  } catch {}
}

export function useCallingBalance() {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);
  const [authReady, setAuthReady] = useState(false);

  // Soft reset of local balance state without touching the auth session.
  // Signing the user out here would race with a freshly-restored session
  // (e.g. right after OTP verification) and bounce the user back to /login.
  const resetBalanceState = () => {
    clearBalanceCache(userId || undefined);
    queryClient.removeQueries({ queryKey: ["calling-balance"] });
  };

  // Use onAuthStateChange for reliable, reactive user tracking
  // This avoids getSession() returning stale localStorage data
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id || null);
      setAuthReady(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const newUserId = session?.user?.id || null;
      setUserId(prev => {
        if (prev !== newUserId) {
          // User changed - wipe all balance cache to prevent cross-user bleed
          clearBalanceCache(prev || undefined);
          queryClient.removeQueries({ queryKey: ["calling-balance"] });
        }
        return newUserId;
      });

      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        clearBalanceCache(newUserId || undefined);
        queryClient.invalidateQueries({ queryKey: ["calling-balance", newUserId] });
      }

      setAuthReady(true);
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  // Real-time subscription to calling_credit for instant balance updates
  useEffect(() => {
    if (!userId) return;
    const refreshBalance = () => {
      clearBalanceCache(userId);
      queryClient.invalidateQueries({ queryKey: ["calling-balance", userId] });
      queryClient.refetchQueries({ queryKey: ["calling-balance", userId], type: "active" });
    };

    window.addEventListener('elefa:calling-balance-changed', refreshBalance);

    const channel = supabase
      .channel(`balance-${userId}-${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'calling_credit', filter: `user_id=eq.${userId}` },
        refreshBalance
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'calling_ledger', filter: `user_id=eq.${userId}` },
        refreshBalance
      )
      .subscribe();

    return () => {
      window.removeEventListener('elefa:calling-balance-changed', refreshBalance);
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  const { data, error, refetch } = useQuery({
    queryKey: ["calling-balance", userId],
    queryFn: async (): Promise<BalanceResponse> => {
      if (!userId) {
        return EMPTY_BALANCE_RESPONSE;
      }

      const getAccessToken = async () => {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        let accessToken = sessionData.session?.access_token;
        let sessionUser = sessionData.session?.user;

        if (!accessToken || !sessionUser?.id) {
          const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) throw refreshError;
          accessToken = refreshed.session?.access_token;
          sessionUser = refreshed.session?.user;
        }

        if (!accessToken || !sessionUser?.id) {
          throw new Error("AUTH_REQUIRED");
        }

        return accessToken;
      };

      const invokeBalance = async (accessToken: string) => {
        const { data, error } = await supabase.functions.invoke('elefa-connect-balance', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: {},
        });

        if (error) {
          throw error;
        }

        if (!data?.success || !data?.balance) {
          throw new Error("BALANCE_RESPONSE_INVALID");
        }

        return data as BalanceResponse;
      };

      try {
        const accessToken = await getAccessToken();
        return await invokeBalance(accessToken);
      } catch (initialError) {
        const message = initialError instanceof Error ? initialError.message : String(initialError);
        const isUnauthorized = /401|unauthorized|auth/i.test(message);

        if (!isUnauthorized) {
          console.error('Balance fetch error:', initialError);
          throw initialError;
        }

        const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !refreshed.session?.access_token || !refreshed.session?.user?.id) {
          console.warn('Balance auth expired; clearing local session state.');
          resetBalanceState();
          return EMPTY_BALANCE_RESPONSE;
        }

        try {
          return await invokeBalance(refreshed.session.access_token);
        } catch (retryError) {
          const retryMessage = retryError instanceof Error ? retryError.message : String(retryError);
          const retryUnauthorized = /401|unauthorized|auth/i.test(retryMessage);

          if (retryUnauthorized) {
            console.warn('Balance auth retry failed; clearing local session state.');
            resetBalanceState();
            return EMPTY_BALANCE_RESPONSE;
          }

          console.error('Balance fetch error after refresh:', retryError);
          throw retryError;
        }
      }
    },
    enabled: authReady && !!userId,
    retry: 3,
    refetchInterval: 120000,
    staleTime: 5_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const hasResolvedBalance = !userId || !!data?.success;
  const isBalancePending = !authReady || (!!userId && !data?.success && !error);

  return {
    balance: data?.balance || EMPTY_BALANCE,
    transactions: data?.transactions || [],
    profile: data?.profile,
    hasResolvedBalance,
    isLoading: isBalancePending,
    error,
    refetch,
  };

}
